import {ViewPopup} from '../ViewPopup.ts'
import {createDivIn, createExternalImgIn, createImgIn, createSpanIn, setElementInnerHTML, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {LobbyImage} from '@colonist/types'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints.ts'
import {CA} from '../../analytics/Analytics.ts'
import {navigateToPage} from '../../ui_utils/ClientUtils.ts'
import {StorageWrapper} from '../../StorageWrapper.ts'
import {AudioManager, AudioType} from '@colonist/audio'
import {ClientUserState} from '../../ClientUserState.ts'
import LogoMobile from '@static/images/logo_mobile.svg'
import BackgroundOverlay from '@static/images/landing_page_background_overlay_mobile.png'
import GoogleIcon from '@static/images/icon_google_color.svg'
import DiscordIcon from '@static/images/icon_discord_color.svg'

const LOGIN_FEATURES = [
    {image: LobbyImage.IconSwords, textKey: 'strings:popups.login.features.playRanked'},
    {image: LobbyImage.Memberships, textKey: 'strings:popups.login.features.trackProgress'},
    {image: LobbyImage.Avatars, textKey: 'strings:popups.login.features.challengeFriends'},
] as const

export class ViewPopupLogin extends ViewPopup {
    private readonly loginButtonsDiv: HTMLDivElement
    private readonly callbackHash?: string | undefined

    // TODO: This file should not be in memebership_incentive folder. Refactor
    constructor(preventClicksOutsideOfPopupFromClosingPopup: boolean = false, callbackHash?: string) {
        super({key: 'strings:homePage.header.login'}, {key: 'strings:colonistCommon.utils.emptyString'}, () => undefined, () => undefined, true)

        this.preventClicksOutsideOfPopupFromClosingPopup = preventClicksOutsideOfPopupFromClosingPopup
        this.callbackHash = callbackHash ?? ViewPopupLogin.getCallbackHashFromUrl()
        setElementInnerHTML(this.body, {key: 'strings:colonistCommon.utils.emptyString'})

        this.loginButtonsDiv = ViewPopupLogin.buildLoginPopupBody(this)
        this.createLoginButtons()
        this.removeFooter()
        this.show()
    }

    private createLoginButtons() {
        ViewPopupLogin.createLoginProviderButtons(this.loginButtonsDiv, this.callbackHash)

        if(ClientUserState.userState?.isLoggedIn) return
        ViewPopupLogin.createContinueAsGuestButton(this.loginButtonsDiv, this)
    }

    static buildLoginPopupBody(popup: ViewPopup): HTMLDivElement {
        popup.contentContainer.classList.add('popup-login')
        popup.body.className = 'popup-login-body'

        createExternalImgIn(popup.body, BackgroundOverlay, {key: 'strings:colonistCommon.utils.emptyString'}, 'popup-login-background-overlay')
        createExternalImgIn(popup.body, LogoMobile, {key: 'strings:colonistCommon.utils.emptyString'}, 'login-logo')
        createSpanIn(popup.body, 'login-tagline', {key: 'strings:popups.login.tagline'})

        const featuresDiv = createDivIn(popup.body, 'login-features')
        for(const feature of LOGIN_FEATURES) {
            const featureDiv = createDivIn(featuresDiv, 'login-feature-item')
            createImgIn(featureDiv, feature.image)
            createSpanIn(featureDiv, '', {key: feature.textKey})
        }

        return createDivIn(popup.body, 'popup-login-wrapper')
    }

    static createContinueAsGuestButton(container: HTMLElement, popup: ViewPopup) {
        const continueAsGuest = createDivIn(container, 'popup-login-btn popup-login-continue-btn')
        createSpanIn(continueAsGuest, 'popup-login-continue-btn-text', {key: 'strings:popups.login.continueAsGuest'})

        setElementOnClick(continueAsGuest, () => {
            AudioManager.play(AudioType.Click)
            popup.removePopup()
        })
    }

    static createLoginProviderButtons(container: HTMLElement, callbackHash?: string) {
        const googleLogin = createDivIn(container, 'popup-login-btn login-btn-provider')
        createExternalImgIn(googleLogin, GoogleIcon, {key: 'strings:colonistCommon.utils.emptyString'}, 'google-icon')
        createSpanIn(googleLogin, '', {key: 'strings:popups.login.continueWith', options: {context: 'google'}})

        const discordLogin = createDivIn(container, 'popup-login-btn login-btn-provider')
        createExternalImgIn(discordLogin, DiscordIcon, {key: 'strings:colonistCommon.utils.emptyString'}, 'discord-icon')
        createSpanIn(discordLogin, '', {key: 'strings:popups.login.continueWith', options: {context: 'discord'}})

        const appleLogin = createDivIn(container, 'popup-login-btn login-btn-provider')
        createImgIn(appleLogin, LobbyImage.AppleIcon)
        createSpanIn(appleLogin, '', {key: 'strings:popups.login.continueWith', options: {context: 'apple'}})

        setElementOnClick(googleLogin, ViewPopupLogin.createLoginClickHandler(UIAPIEndpoints.googleAuth(callbackHash)))
        setElementOnClick(discordLogin, ViewPopupLogin.createLoginClickHandler(UIAPIEndpoints.discordAuth(callbackHash)))
        setElementOnClick(appleLogin, ViewPopupLogin.createLoginClickHandler(UIAPIEndpoints.appleAuth(callbackHash)))
    }

    private static getCallbackHashFromUrl(): string | undefined {
        const hash = window.location.hash
        if(!hash) return undefined
        if(hash.length <= 1) return undefined
        return hash.substring(1)
    }

    static createLoginClickHandler(url: string) {
        return () => {
            AudioManager.play(AudioType.Click)
            CA.sendGoogleTagManagerEvent(CA.GoogleTagManagerEvent.UserClickedLoginButton)
            CA.sendGoogleAnalyticsEvent(CA.Category.HeaderProfile, CA.Label.HeaderProfileClickedLogin)
            StorageWrapper.clearUserState()
            navigateToPage(url)
        }
    }
}
